import { useMemo } from 'react';
import { Link } from 'react-router';
import type { Professional } from 'generated/graphql';
import type { MeUser } from '~/server/types';
import { useUserLoggedIn } from '~/modules/user';
import { useConversation } from '../../context';
import type { Conversation, Participant } from '../../types';
import { getEnquiryConversationProfessional } from '../../utils';

/**
 * Banner shown when chat details are being viewed from the professional side of
 * an enquiry. Returns `null` when the chat details are being viewed from the
 * enquirer side (from the personal inbox).
 * @see {@link ChatDetails}
 * @see {@link ChatBox}
 */
export function ChatProfessionalBanner() {
    const { professionalID, conversation, selfParticipant } = useConversation();
    const { user, isLoggedIn } = useUserLoggedIn();
    const bannerDetails = useMemo(() => {
        return getBannerDetails({
            conversation,
            professionalID,
            selfParticipant,
            user: isLoggedIn ? user : undefined,
        });
    }, [conversation, professionalID, selfParticipant, isLoggedIn, user]);

    if (!bannerDetails) {
        return null;
    }

    const {
        professionalID: bannerProfessionalID,
        professional,
        message,
    } = bannerDetails;

    return (
        <div className="flex items-center gap-[8px] rounded-[8px] bg-gray-100 px-[12px] py-[8px] text-[13px] leading-normal text-black/80">
            {professional?.CompanyLogo && (
                <img
                    src={professional.CompanyLogo}
                    alt={professional.Title}
                    className="size-[28px] shrink-0 rounded-full object-contain"
                    style={{
                        backgroundColor:
                            professional.LogoBackgroundColor ?? 'white',
                    }}
                />
            )}
            <div className="min-w-0 flex-1">
                <span>{message} </span>
                {professional ? (
                    <Link
                        to={professional.Link}
                        className="font-medium text-black hover:underline"
                    >
                        {professional.Title}
                    </Link>
                ) : (
                    <span className="font-medium text-black">{`Professional #${bannerProfessionalID}`}</span>
                )}
            </div>
        </div>
    );
}

interface BannerDetails {
    professionalID: number;
    professional: Pick<
        Professional,
        'Title' | 'Link' | 'CompanyLogo' | 'LogoBackgroundColor'
    > | null;
    message: string;
}

/**
 * Get the professional details and message to display in the banner. This only
 * returns a value when the user is on the professional side of an enquiry conversation.
 */
function getBannerDetails({
    conversation,
    professionalID,
    selfParticipant,
    user,
}: {
    conversation: Conversation | undefined;
    professionalID: number | null;
    selfParticipant: Participant | null;
    user?: MeUser;
}): BannerDetails | null {
    if (professionalID && professionalID > 0) {
        // Company inbox: get professional participant.
        const participant = conversation?.Participants.find(
            ({ Identity }) =>
                Identity.__typename === 'Professional' &&
                Identity.ID === professionalID,
        );
        const participantProfessional =
            participant?.Identity.__typename === 'Professional'
                ? participant.Identity
                : null;
        return {
            professionalID,
            professional:
                participantProfessional ??
                // Fallback to user professionals if participants not found / not loaded yet.
                user?.Professionals.find(
                    (professional) => professional.ID === professionalID,
                ) ??
                null,
            message: 'Messaging on behalf of',
        };
    } else if (
        conversation?.__typename === 'EnquiryConversation' &&
        selfParticipant?.__typename === 'EnquiryParticipant' &&
        selfParticipant.Identity.__typename === 'Member' &&
        selfParticipant.IsCollaborator
    ) {
        // Collaborator: get enquiry professional.
        const professional = getEnquiryConversationProfessional(conversation);
        return {
            professionalID: professional.ID,
            professional,
            message: 'You were invited to this chat by',
        };
    }
    return null;
}
